import { ContextMenu, ContextMenuItem, EmptyState } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFullscreen } from '../../../hooks/useFullscreen';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { GridWeek } from '../GridWeek/GridWeek';
import styles from './GridRows.module.scss';

export const GridRows = observer(() => {
  const { t } = useTranslation();
  const isFullscreen = useFullscreen();

  const [weekWidth, setWeekWidth] = useState<number>(0);

  const store = useTeachingBlockStore();

  const contextMenu: ContextMenuItem[] = [
    {
      node: (
        <div className={styles['menu-name']}>{store.selectedRows.map((selected) => selected.label).join(', ')}</div>
      ),
      type: 'component',
    },
    {
      type: 'ruler',
    },
    {
      label: t('pinboard.actions.lockAll'),
      onClick: (e) => {
        e.stopPropagation();
        store.lockCardsInSelectedRows();
      },
    },
    {
      label: t('pinboard.actions.unlockAll'),
      onClick: (e) => {
        e.stopPropagation();
        store.unlockCardsInSelectedRows();
      },
    },
    {
      label: t('pinboard.actions.removeAll'),
      type: 'error',
      onClick: (e) => {
        e.stopPropagation();
        store.discardCardsInSelectedRows();
      },
    },
  ];

  const weekRef = useRef<HTMLDivElement>(null);

  const today = dayjs().utc().startOf('day');
  const isInCurrentYear = today.isSameOrAfter([...store.dates.keys()][0], 'day');
  const distance = today.diff([...store.dates.keys()][0], 'weeks');
  const offset = today.day();

  const timeframeWidth = weekWidth * distance + (weekWidth / 7) * offset;

  const classes = classNames(styles['grid-rows'], {
    [styles['is-fullscreen']]: isFullscreen,
  });

  const weekRefWidth = weekRef.current?.getBoundingClientRect().width;

  useEffect(() => {
    setWeekWidth(weekRefWidth ?? 0);
  }, [weekRefWidth]);

  return (
    <div className={classes}>
      {store.rows?.length === 0 ? (
        <EmptyState
          title={t('pinboard.empty', { type: t('classes.title', { count: 2 }) })}
          padding='l'
          className={styles.empty}
        />
      ) : (
        <>
          {isInCurrentYear && !!store.currentVersion.active && (
            <div
              title={t('pinboard.pastTimeframe')}
              className={styles['today-indicator']}
              style={{ width: `${timeframeWidth}px` }}
            ></div>
          )}
          {store.rows?.map((row) => {
            return (
              <div key={row.value} className={styles.row}>
                <ContextMenu data={contextMenu} usePortal={!isFullscreen}>
                  <div
                    className={styles['name-wrapper']}
                    onClick={(event) => {
                      store.rowClickHandler(event, { label: row.label, value: row.value });
                    }}
                    title={row.label}
                    onContextMenu={() => {
                      if (store.selectedRows.length <= 1 || !store.selectedRows.includes(row)) store.selectRows([row]);
                    }}
                  >
                    <div
                      className={classNames(styles.name, {
                        [styles.selected]: store.selectedRows.find((r) => r.value === row.value),
                      })}
                    >
                      {row.label}
                    </div>
                  </div>
                </ContextMenu>

                {Array.from(store.weeks.entries()).map(([weekNumber, weekDays], index) => {
                  const firstDay = weekDays[0];
                  return (
                    <GridWeek
                      key={weekNumber + '_' + index}
                      row={row}
                      weekNumber={firstDay.weekNumber}
                      year={dayjs(firstDay.dateString).year()}
                      days={weekDays}
                      weekRef={index === 0 ? weekRef : undefined}
                    />
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
});
